"use client";

import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { messagingApi } from "@/lib/api/services/messaging.api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { MessageSquare, Search } from "lucide-react";
import Link from "next/link";
import { useState } from "react";

interface Conversation {
  id: string;
  title: string;
  participants: string[];
  lastMessage?: string;
  lastMessageAt?: string;
  unreadCount?: number;
}

export default function MessagesPage() {
  const [searchQuery, setSearchQuery] = useState("");

  const { data: conversations, isLoading } = useQuery({
    queryKey: ["conversations"],
    queryFn: async () => {
      const data = await messagingApi.listConversations();
      return ((data as unknown as { data?: Conversation[] }).data ?? []) as Conversation[];
    },
  });

  const filtered = useMemo(() => {
    if (!conversations) return [];
    if (!searchQuery.trim()) return conversations;
    const q = searchQuery.toLowerCase();
    return conversations.filter(
      (c) => c.title.toLowerCase().includes(q) || c.participants.some((p) => p.toLowerCase().includes(q))
    );
  }, [conversations, searchQuery]);

  return (
    <div className="space-y-6">
      <h1 className="font-heading text-xl font-semibold tracking-tight text-foreground">Messages</h1>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-4 p-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-muted/10 animate-pulse shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-1/3 bg-muted/10 rounded animate-pulse" />
                    <div className="h-3 w-1/2 bg-muted/10 rounded animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center">
              <MessageSquare className="mx-auto h-10 w-10 text-muted-foreground/30" />
              <h3 className="mt-4 font-heading text-base font-semibold text-foreground">
                {searchQuery ? "No results" : "No conversations"}
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {searchQuery ? "Try a different search term." : "Start a conversation to connect with others."}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border/60">
              {filtered.map((conversation) => (
                <Link
                  key={conversation.id}
                  href={`/messages/${conversation.id}`}
                  className="flex items-center gap-3 p-4 transition-colors hover:bg-muted/10"
                >
                  <div className="relative shrink-0">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <MessageSquare className="h-5 w-5" />
                    </div>
                    {conversation.unreadCount && conversation.unreadCount > 0 && (
                      <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-[0.6875rem] font-bold text-destructive-foreground tabular-nums">
                        {conversation.unreadCount}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">{conversation.title}</p>
                    {conversation.lastMessage && (
                      <p className="text-xs text-muted-foreground truncate mt-0.5">{conversation.lastMessage}</p>
                    )}
                  </div>
                  {conversation.lastMessageAt && (
                    <p className="text-xs text-muted-foreground shrink-0">{conversation.lastMessageAt}</p>
                  )}
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
